'use client';

import { Button } from '@/components/ui/button';
import { FileIcon, TrashIcon, UploadIcon } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { FieldValues, Path, PathValue, UseFormSetValue } from 'react-hook-form';

interface FileInputProps<T extends FieldValues> {
  setValue: UseFormSetValue<T>;
  name: string;
  error?: string;
  accept?: string;
  value?: File;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
}

export function FileInput<T extends FieldValues>({
  setValue,
  name,
  error,
  accept = '*/*',
  value,
  placeholder = 'Seleccionar archivo',
  helperText,
  disabled = false,
}: FileInputProps<T>) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (error) {
      setValue(name as Path<T>, undefined as PathValue<T, Path<T>>);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [error, name, setValue]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (accept !== '*/*' && !isFileTypeValid(file, accept)) {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setValue(name as any, file as any);
    }
  };

  const handleRemoveFile = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setValue(name as any, undefined as any);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleButtonClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isFileTypeValid = (file: File, acceptTypes: string): boolean => {
    const types = acceptTypes.split(',').map((type) => type.trim());

    return types.some((type) => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      } else if (type.includes('*')) {
        const baseType = type.split('/')[0];
        return file.type.startsWith(baseType + '/');
      } else {
        return file.type === type;
      }
    });
  };

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  }

  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (accept !== '*/*' && !isFileTypeValid(file, accept)) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setValue(name as any, file as any);
    }
  }

  return (
    <div className="w-full">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-6 transition-colors ${
          disabled
            ? 'border-gray-200 bg-gray-50'
            : error
              ? 'border-red-300 bg-red-50'
              : isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />

        {!value ? (
          <div className="flex flex-col items-center gap-4">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                disabled ? 'bg-gray-100' : error ? 'bg-red-100' : 'bg-blue-100'
              }`}
            >
              <UploadIcon
                size={24}
                className={disabled ? 'text-gray-400' : error ? 'text-red-500' : 'text-blue-600'}
              />
            </div>
            <div className="text-center">
              <p
                className={`text-sm font-medium mb-1 ${
                  disabled ? 'text-gray-400' : 'text-gray-900'
                }`}
              >
                Arrastra tu archivo aquí
              </p>
              <p className={`text-xs mb-3 ${disabled ? 'text-gray-300' : 'text-gray-500'}`}>
                o haz clic para seleccionar
              </p>
              <Button onClick={handleButtonClick} variant="outline" size="sm" disabled={disabled}>
                {placeholder}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <FileIcon size={16} className="text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900 text-sm">{value.name}</p>
                <p className="text-xs text-gray-500">{formatFileSize(value.size)}</p>
              </div>
            </div>
            {!disabled && (
              <Button
                onClick={handleRemoveFile}
                variant="clear"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <TrashIcon size={14} />
              </Button>
            )}
          </div>
        )}
      </div>

      {helperText && !error && <p className="text-xs text-gray-500 mt-1">{helperText}</p>}

      {error && <p className="text-xs text-red-500 mt-1 whitespace-pre-line">{error}</p>}
    </div>
  );
}
